import { Alert02Icon, CheckmarkCircle02Icon, HelpCircleIcon } from '@hugeicons/core-free-icons';
import { Icon } from '../primitives/icon.js';
import * as React from 'react';
import { cn } from '../../lib/cn.js';
import { formatDateTime, formatRelative } from '../../lib/format.js';

export type Freshness = 'fresh' | 'stale' | 'unknown';

export interface FreshnessIndicatorProps {
  /** ISO time the data was last verified against its source; null = never. */
  verifiedAt: string | null;
  /** Older than this is stale (default 24 h). */
  staleAfterMinutes?: number;
  /** Force a state (e.g. the server already computed it). */
  state?: Freshness;
  /** e.g. "CRM", "Inventario". Adds "en {source}". */
  source?: string;
  timezone?: string;
  now?: Date;
  className?: string;
}

export function computeFreshness(verifiedAt: string | null, staleAfterMinutes: number, now: Date = new Date()): Freshness {
  if (!verifiedAt) return 'unknown';
  const t = new Date(verifiedAt).getTime();
  if (Number.isNaN(t)) return 'unknown';
  return now.getTime() - t > staleAfterMinutes * 60_000 ? 'stale' : 'fresh';
}

/** "Verificado hace 5 minutos" with fresh / stale / unknown cue. */
export const FreshnessIndicator = React.forwardRef<HTMLSpanElement, FreshnessIndicatorProps>(function FreshnessIndicator(
  { verifiedAt, staleAfterMinutes = 24 * 60, state, source, timezone = 'America/Mexico_City', now, className },
  ref,
) {
  const s = state ?? computeFreshness(verifiedAt, staleAfterMinutes, now);
  const rel = verifiedAt ? formatRelative(verifiedAt, now) : null;
  const where = source ? ` en ${source}` : '';
  const text =
    s === 'unknown' || !rel
      ? `Sin verificar${where}`
      : s === 'stale'
        ? `Desactualizado · verificado${where} ${rel}`
        : `Verificado${where} ${rel}`;
  const statusIcon = s === 'fresh' ? CheckmarkCircle02Icon : s === 'stale' ? Alert02Icon : HelpCircleIcon;
  return (
    <span
      ref={ref}
      title={verifiedAt ? formatDateTime(verifiedAt, timezone) : undefined}
      className={cn(
        'inline-flex items-center gap-1 text-meta',
        s === 'fresh' ? 'text-text-secondary' : s === 'stale' ? 'font-medium text-warning' : 'text-text-secondary',
        className,
      )}
    >
      <Icon icon={statusIcon} className={cn('size-3.5 shrink-0', s === 'fresh' && 'text-success')} />
      {text}
    </span>
  );
});
